import { ScrollArea, ScrollAreaViewport, ScrollAreaScrollbar } from '@/components/ui/scroll-area';
import { Database, Play, Loader2, Info, Search, RefreshCw, Copy, FileJson, Link, Table, ChevronDown, ChevronUp } from 'lucide-react';
import { useState, useEffect, useMemo, useRef } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { TreeView, countTreeMatches } from '@/components/ui/tree-view';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';
import { getNodeDefinition } from '@/utils/nodeDefinitions';
import { NodeIcon } from '@/components/ui/node-icon';
import { CompactModeToggle } from './CompactModeToggle';
import { InputPanelSkeleton } from './LoadingSkeletons';

interface InputData {
  nodeId: string;
  nodeName: string;
  nodeType?: string; // Node type for icon lookup
  nodeDistance: number; // How many nodes back
  data: any;
  sourceHandle?: string;
  // Virtual item properties - used for iterating nodes to show "current item"
  isVirtualItem?: boolean;
  virtualItemLabel?: string;
}

interface NodeInputPanelProps {
  inputData: InputData[];
  onExecutePrevious?: () => void;
  isExecuting?: boolean;
  isPreviousNodeTrigger?: boolean;
  isFirstNode?: boolean;
  currentNodeId?: string;
}

export function NodeInputPanel({
  inputData,
  onExecutePrevious,
  isExecuting = false,
  isPreviousNodeTrigger = false,
  isFirstNode = false,
  currentNodeId
}: NodeInputPanelProps) {
  const [activeTab, setActiveTab] = useState<'schema' | 'json' | 'table'>('schema');
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedNodes, setExpandedNodes] = useState<Set<string>>(new Set());
  const [isCompact, setIsCompact] = useState(false);

  // Track the current node to detect navigation
  const prevNodeIdRef = useRef<string | undefined>(undefined);
  // Track previous executing state to detect when execution completes
  const wasExecutingRef = useRef(false);

  // Auto-expand first node (and close others) on initial load, navigation, or execution complete
  useEffect(() => {
    if (inputData.length > 0) {
      const isFirstLoad = prevNodeIdRef.current === undefined;
      const isNavigation = currentNodeId !== undefined && prevNodeIdRef.current !== currentNodeId;
      const executionJustCompleted = wasExecutingRef.current && !isExecuting;

      // Update tracked state
      prevNodeIdRef.current = currentNodeId;
      wasExecutingRef.current = isExecuting;

      // On first load, navigation, or execution complete: expand first node, close all others
      if (isFirstLoad || isNavigation || executionJustCompleted) {
        const sortedData = [...inputData].sort((a, b) => a.nodeDistance - b.nodeDistance);
        if (sortedData[0]) {
          const firstKey = sortedData[0].sourceHandle
            ? `${sortedData[0].nodeId}:${sortedData[0].sourceHandle}`
            : sortedData[0].nodeId;
          setExpandedNodes(new Set([firstKey]));
        }
      }
    }
  }, [inputData, currentNodeId, isExecuting]);

  const toggleNodeExpansion = (nodeId: string) => {
    setExpandedNodes(prev => {
      const newSet = new Set(prev);
      if (newSet.has(nodeId)) {
        newSet.delete(nodeId);
      } else {
        newSet.add(nodeId);
      }
      return newSet;
    });
  };

  // Sort input data by distance (closest first = reverse order)
  const sortedInputData = [...inputData].sort((a, b) => a.nodeDistance - b.nodeDistance);

  // Calculate total match count across all nodes when searching
  const totalMatchCount = useMemo(() => {
    if (!searchQuery) return 0;
    return sortedInputData.reduce((total, input) => {
      return total + countTreeMatches(input.data, searchQuery);
    }, 0);
  }, [sortedInputData, searchQuery]);

  // Show skeleton when executing (loading state)
  if (isExecuting) {
    return <InputPanelSkeleton />;
  }

  // Empty state - no input data yet
  if (inputData.length === 0) {
    // Special message for first node
    if (isFirstNode) {
      return (
        <div className="h-full flex flex-col items-center justify-center p-6">
          <div className="text-center max-w-sm">
            <Database className="h-12 w-12 text-muted-foreground mx-auto mb-3 opacity-40" />
            <h3 className="text-sm font-medium text-foreground mb-2">
              First node in workflow
            </h3>
            <p className="text-xs text-muted-foreground">
              This is the starting point of your workflow. There are no previous nodes to provide input data.
            </p>
          </div>
        </div>
      );
    }
    
    return (
      <div className="h-full flex flex-col items-center justify-center p-6">
        <div className="text-center max-w-sm">
          <Database className="h-12 w-12 text-muted-foreground mx-auto mb-3 opacity-40" />
          <h3 className="text-sm font-medium text-foreground mb-2">
            {isPreviousNodeTrigger ? 'Trigger test data' : 'No input data yet'}
          </h3>
          <p className="text-xs text-muted-foreground mb-4">
            {isPreviousNodeTrigger
              ? 'Run the trigger to get test data for this node'
              : 'Execute previous nodes to see their output data here'}
          </p>

          {onExecutePrevious && (
            <>
              <Button
                onClick={() => onExecutePrevious()}
                disabled={isExecuting}
                size="sm"
                className="mb-3"
              >
                {isExecuting ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Executing...
                  </>
                ) : (
                  <>
                    <Play className="w-4 h-4 mr-2" />
                    {isPreviousNodeTrigger ? 'Execute from trigger' : 'Execute previous nodes'}
                  </>
                )}
              </Button>

              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <div className="flex items-center justify-center gap-1 text-xs text-muted-foreground cursor-help">
                      <Info className="w-3 h-3" />
                      <span>{isPreviousNodeTrigger ? 'Uses trigger test input' : 'From the earliest node that needs it'}</span>
                    </div>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p className="max-w-xs text-xs">
                      {isPreviousNodeTrigger
                        ? 'This will execute the workflow from the trigger using its configured test input data. You can edit the test input in the trigger node settings.'
                        : 'This will execute all nodes from the workflow trigger up to (but not including) the current node, so you can see what data is available.'}
                    </p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </>
          )}
        </div>
      </div>
    );
  }

  // Has input data - show all nodes in reverse order
  return (
    <div className="h-full w-full flex flex-col bg-muted/30 my-2 mx-2 rounded-lg shadow-inner border border-border/50">
      {/* Header with Schema/JSON/Search/Refresh buttons */}
      <div className="flex-shrink-0 border-b border-border bg-background/50 px-3 py-2">
        <div className="flex items-center justify-between gap-2">
          <h3 className="text-sm font-semibold text-foreground">INPUT</h3>
          
          <div className="flex items-center gap-1">
            {/* Schema/JSON/Table Toggle Buttons */}
            <div className="flex items-center border rounded-md bg-background">
              <Button
                variant={activeTab === 'schema' ? 'default' : 'ghost'}
                size="sm"
                className="h-7 text-xs rounded-r-none"
                onClick={() => setActiveTab('schema')}
              >
                Schema
              </Button>
              <Button
                variant={activeTab === 'json' ? 'default' : 'ghost'}
                size="sm"
                className="h-7 text-xs rounded-none border-l border-r"
                onClick={() => setActiveTab('json')}
              >
                JSON
              </Button>
              <Button
                variant={activeTab === 'table' ? 'default' : 'ghost'}
                size="sm"
                className="h-7 text-xs rounded-l-none"
                onClick={() => setActiveTab('table')}
              >
                Table
              </Button>
            </div>

            {/* Compact Mode Toggle */}
            <CompactModeToggle 
              isCompact={isCompact} 
              onToggle={() => setIsCompact(!isCompact)} 
            />

            {/* Expand/Collapse All Buttons - Only show in schema view */}
            {activeTab === 'schema' && (
              <>
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-7 w-7 p-0"
                        onClick={() => {
                          const allNodeKeys = sortedInputData.map(item => 
                            item.sourceHandle ? `${item.nodeId}:${item.sourceHandle}` : item.nodeId
                          );
                          setExpandedNodes(new Set(allNodeKeys));
                        }}
                      >
                        <ChevronDown className="w-3.5 h-3.5" />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p className="text-xs">Expand all nodes</p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>

                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-7 w-7 p-0"
                        onClick={() => setExpandedNodes(new Set())}
                      >
                        <ChevronUp className="w-3.5 h-3.5" />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p className="text-xs">Collapse all nodes</p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </>
            )}

            {/* Search Button */}
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant={searchOpen ? 'default' : 'ghost'}
                    size="sm"
                    className="h-7 w-7 p-0"
                    onClick={() => setSearchOpen(!searchOpen)}
                  >
                    <Search className="w-3.5 h-3.5" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>
                  <p className="text-xs">Search fields</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>

            {/* Refresh Button */}
            {onExecutePrevious && (
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      onClick={() => onExecutePrevious()}
                      disabled={isExecuting}
                      size="sm"
                      variant="ghost"
                      className="h-7 w-7 p-0"
                    >
                      {isExecuting ? (
                        <Loader2 className="w-3.5 h-3.5 animate-spin" />
                      ) : (
                        <RefreshCw className="w-3.5 h-3.5" />
                      )}
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p className="text-xs">Refresh input data</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            )}
          </div>
        </div>

        {/* Search Box (collapsible) */}
        {searchOpen && (
          <div className="mt-2 space-y-2">
            <Input
              placeholder="Search fields..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="h-8 text-xs"
              autoFocus
            />
            {searchQuery && (
              <div className="text-xs text-muted-foreground">
                {totalMatchCount === 0 ? (
                  <span>No matches found</span>
                ) : (
                  <span>
                    Found <span className="font-semibold text-foreground">{totalMatchCount}</span> {totalMatchCount === 1 ? 'match' : 'matches'} across {sortedInputData.length} {sortedInputData.length === 1 ? 'node' : 'nodes'}
                  </span>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Content - Show all nodes in reverse order */}
      <ScrollArea className="flex-1">
        <ScrollAreaViewport>
          <div className="p-3 space-y-2">
            {activeTab === 'schema' ? (
              // Schema View - All nodes with TreeView
              sortedInputData.map((input, index) => {
                // Create unique key for each entry (nodeId + sourceHandle + virtualItem marker)
                const uniqueKey = input.isVirtualItem
                  ? `${input.nodeId}:item`
                  : input.sourceHandle
                  ? `${input.nodeId}:${input.sourceHandle}`
                  : input.nodeId;
                const isExpanded = expandedNodes.has(uniqueKey);
                const isFirst = index === 0;
                const nodeDef = input.nodeType ? getNodeDefinition(input.nodeType) : null;

                return (
                  <div
                    key={uniqueKey}
                    className={cn(
                      "border rounded-lg overflow-hidden bg-background",
                      input.isVirtualItem && "ml-4 border-dashed border-purple-300 dark:border-purple-700"
                    )}
                  >
                    {/* Node Header */}
                    <button
                      onClick={() => toggleNodeExpansion(uniqueKey)}
                      className={cn(
                        "w-full p-3 border-b hover:bg-muted/70 transition-colors flex items-center justify-between",
                        input.isVirtualItem ? "bg-purple-50/50 dark:bg-purple-950/20" : "bg-muted/50"
                      )}
                    >
                      <div className="flex items-center gap-2">
                        <div
                          className={cn(
                            "flex items-center justify-center w-6 h-6 rounded-md transition-transform duration-200",
                            isExpanded && "rotate-90"
                          )}
                          style={{ backgroundColor: input.isVirtualItem ? '#8b5cf6' : (nodeDef?.bgColor || '#f1f5f9') }}
                        >
                          {input.isVirtualItem ? (
                            <span className="text-white text-xs font-bold">{ }</span>
                          ) : (
                            <NodeIcon
                              icon={nodeDef?.icon || Database}
                              iconType={nodeDef?.iconType}
                              iconColor={nodeDef?.iconColor}
                              color={nodeDef?.color}
                              className="w-4 h-4"
                            />
                          )}
                        </div>
                        <span className="text-sm font-medium">
                          {input.isVirtualItem ? (
                            <>
                              <span className="text-muted-foreground">{input.nodeName} → </span>
                              <span className="text-purple-600 dark:text-purple-400">{input.virtualItemLabel || 'item'}</span>
                            </>
                          ) : (
                            input.nodeName
                          )}
                        </span>
                        {input.isVirtualItem && (
                          <span className="text-xs text-purple-500 bg-purple-100 dark:bg-purple-900/50 px-1.5 py-0.5 rounded">
                            drag for current item
                          </span>
                        )}
                      </div>
                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-7 w-7 p-0"
                              onClick={(e) => {
                                e.stopPropagation();
                                navigator.clipboard.writeText(JSON.stringify(input.data, null, 2));
                              }}
                            >
                              <Copy className="w-3.5 h-3.5" />
                            </Button>
                          </TooltipTrigger>
                          <TooltipContent>
                            <p className="text-xs">Copy all data</p>
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    </button>

                    {/* Node Content */}
                    {isExpanded && (
                      <div className={cn("p-3", isCompact && "p-2")}>
                        <TreeView
                          data={input.data}
                          defaultExpanded={isFirst ? 1 : 0}
                          showTypes={!isCompact}
                          showCopyButtons={!isCompact}
                          showPath={!isCompact}
                          searchQuery={searchQuery}
                          draggable={true}
                          nodeId={input.nodeId}
                          nodeName={input.nodeName}
                          sourceHandle={input.sourceHandle}
                          isVirtualItem={input.isVirtualItem}
                        />
                      </div>
                    )}
                  </div>
                );
              })
            ) : activeTab === 'json' ? (
              // JSON View - All nodes with syntax-highlighted JSON
              sortedInputData.map((input, index) => {
                const uniqueKey = input.sourceHandle 
                  ? `${input.nodeId}:${input.sourceHandle}` 
                  : input.nodeId;
                const isExpanded = expandedNodes.has(uniqueKey);
                const isFirst = index === 0;
                const nodeDef = input.nodeType ? getNodeDefinition(input.nodeType) : null;

                return (
                  <div key={uniqueKey} className="border rounded-lg overflow-hidden bg-background">
                    {/* Node Header */}
                    <button
                      onClick={() => toggleNodeExpansion(uniqueKey)}
                      className="w-full p-3 bg-muted/50 border-b hover:bg-muted/70 transition-colors flex items-center justify-between"
                    >
                      <div className="flex items-center gap-2">
                        <div
                          className={cn(
                            "flex items-center justify-center w-6 h-6 rounded-md transition-transform duration-200",
                            isExpanded && "rotate-90"
                          )}
                          style={{ backgroundColor: nodeDef?.bgColor || '#f1f5f9' }}
                        >
                          <NodeIcon
                            icon={nodeDef?.icon || Database}
                            iconType={nodeDef?.iconType}
                            iconColor={nodeDef?.iconColor}
                            color={nodeDef?.color}
                            className="w-4 h-4"
                          />
                        </div>
                        <span className="text-sm font-medium">
                          {input.nodeName}
                        </span>
                      </div>
                      <div className="flex items-center gap-1">
                        <TooltipProvider>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-7 w-7 p-0"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  navigator.clipboard.writeText(JSON.stringify(input.data, null, 2));
                                }}
                              >
                                <FileJson className="w-3.5 h-3.5" />
                              </Button>
                            </TooltipTrigger>
                            <TooltipContent>
                              <p className="text-xs">Copy JSON</p>
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                        <TooltipProvider>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-7 w-7 p-0"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  navigator.clipboard.writeText(`{{ $node["${input.nodeName}"].json }}`);
                                }}
                              >
                                <Link className="w-3.5 h-3.5" />
                              </Button>
                            </TooltipTrigger>
                            <TooltipContent>
                              <p className="text-xs">Copy reference</p>
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      </div>
                    </button>

                    {/* JSON Content */}
                    {isExpanded && (
                      <div className="p-4 bg-muted/20">
                        {(() => {
                          // Limit large arrays to prevent browser crashes
                          const MAX_ITEMS = 100;
                          let displayData = input.data;
                          let truncationWarning = null;

                          if (Array.isArray(input.data) && input.data.length > MAX_ITEMS) {
                            displayData = input.data.slice(0, MAX_ITEMS);
                            truncationWarning = `... and ${input.data.length - MAX_ITEMS} more items (${input.data.length} total)`;
                          } else if (input.data?.output && Array.isArray(input.data.output) && input.data.output.length > MAX_ITEMS) {
                            displayData = { ...input.data, output: input.data.output.slice(0, MAX_ITEMS) };
                            truncationWarning = `... and ${input.data.output.length - MAX_ITEMS} more items (${input.data.output.length} total)`;
                          }

                          return (
                            <>
                              <pre className="text-xs font-mono overflow-x-auto whitespace-pre-wrap max-h-[600px]">
                                <code
                                  dangerouslySetInnerHTML={{
                                    __html: JSON.stringify(displayData, null, 2)
                                      .replace(/&/g, '&amp;')
                                      .replace(/</g, '&lt;')
                                      .replace(/>/g, '&gt;')
                                      .replace(/"([^"]+)":/g, '<span class="text-blue-400">"$1"</span>:')
                                      .replace(/: "([^"]*)"/g, ': <span class="text-orange-400">"$1"</span>')
                                      .replace(/: (\d+\.?\d*)/g, ': <span class="text-green-400">$1</span>')
                                      .replace(/: (true|false)/g, ': <span class="text-purple-400">$1</span>')
                                      .replace(/: null/g, ': <span class="text-gray-400">null</span>')
                                  }}
                                />
                              </pre>
                              {truncationWarning && (
                                <div className="mt-2 text-xs text-muted-foreground bg-muted/50 p-2 rounded">
                                  {truncationWarning}
                                </div>
                              )}
                            </>
                          );
                        })()}
                      </div>
                    )}
                  </div>
                );
              })
            ) : (
              // Table View - All nodes with table format
              sortedInputData.map((input, index) => {
                const uniqueKey = input.sourceHandle 
                  ? `${input.nodeId}:${input.sourceHandle}` 
                  : input.nodeId;
                const isExpanded = expandedNodes.has(uniqueKey);
                const isFirst = index === 0;
                const nodeDef = input.nodeType ? getNodeDefinition(input.nodeType) : null;

                // Helper function to render table for data
                const renderTable = (data: any) => {
                  if (!data) {
                    return (
                      <div className="text-muted-foreground p-4 text-center text-sm">
                        No data available
                      </div>
                    );
                  }

                  // Handle array of objects
                  if (Array.isArray(data) && data.length > 0 && typeof data[0] === 'object') {
                    const MAX_TABLE_ROWS = 100;
                    const keys = Object.keys(data[0]);
                    const filteredData = searchQuery
                      ? data.filter((row: any) =>
                          Object.values(row).some((val) =>
                            String(val).toLowerCase().includes(searchQuery.toLowerCase())
                          )
                        )
                      : data;
                    const displayData = filteredData.slice(0, MAX_TABLE_ROWS);
                    const totalRows = filteredData.length;

                    return (
                      <div className="overflow-auto max-h-[500px] border rounded-md">
                        <table className="w-full text-sm border-collapse">
                          <thead className="bg-muted sticky top-0 z-10">
                            <tr>
                              {keys.map((key) => (
                                <th key={key} className="border-b border-r last:border-r-0 p-2 text-left font-medium text-xs">
                                  {key}
                                </th>
                              ))}
                            </tr>
                          </thead>
                          <tbody>
                            {displayData.map((row: any, idx: number) => (
                              <tr key={idx} className="hover:bg-muted/50">
                                {keys.map((key) => (
                                  <td key={key} className="border-b border-r last:border-r-0 p-2 text-xs">
                                    {typeof row[key] === 'object'
                                      ? JSON.stringify(row[key])
                                      : String(row[key])}
                                  </td>
                                ))}
                              </tr>
                            ))}
                          </tbody>
                        </table>
                        {filteredData.length === 0 && (
                          <div className="text-center text-muted-foreground p-4 text-sm">
                            No results found for "{searchQuery}"
                          </div>
                        )}
                        {totalRows > MAX_TABLE_ROWS && (
                          <div className="text-center text-muted-foreground p-2 text-xs bg-muted/50 border-t">
                            Showing {MAX_TABLE_ROWS} of {totalRows} rows
                          </div>
                        )}
                      </div>
                    );
                  }

                  // Handle single object - show as key-value table
                  if (typeof data === 'object' && !Array.isArray(data)) {
                    const entries = Object.entries(data);
                    const filteredEntries = searchQuery
                      ? entries.filter(
                          ([key, value]) =>
                            key.toLowerCase().includes(searchQuery.toLowerCase()) ||
                            String(value).toLowerCase().includes(searchQuery.toLowerCase())
                        )
                      : entries;

                    return (
                      <div className="overflow-auto max-h-[500px] border rounded-md">
                        <table className="w-full text-sm border-collapse">
                          <thead className="bg-muted sticky top-0 z-10">
                            <tr>
                              <th className="border-b border-r p-2 text-left font-medium text-xs w-1/3">Key</th>
                              <th className="border-b p-2 text-left font-medium text-xs">Value</th>
                            </tr>
                          </thead>
                          <tbody>
                            {filteredEntries.map(([key, value]) => (
                              <tr key={key} className="hover:bg-muted/50">
                                <td className="border-b border-r p-2 font-medium text-xs">{key}</td>
                                <td className="border-b p-2 text-xs font-mono">
                                  {typeof value === 'object'
                                    ? JSON.stringify(value, null, 2)
                                    : String(value)}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                        {filteredEntries.length === 0 && (
                          <div className="text-center text-muted-foreground p-4 text-sm">
                            No results found for "{searchQuery}"
                          </div>
                        )}
                      </div>
                    );
                  }

                  return (
                    <div className="text-muted-foreground p-4 text-center text-sm">
                      Data is not in a table-compatible format
                    </div>
                  );
                };
                
                return (
                  <div key={uniqueKey} className="border rounded-lg overflow-hidden bg-background">
                    {/* Node Header */}
                    <button
                      onClick={() => toggleNodeExpansion(uniqueKey)}
                      className="w-full p-3 bg-muted/50 border-b hover:bg-muted/70 transition-colors flex items-center justify-between"
                    >
                      <div className="flex items-center gap-2">
                        <div
                          className={cn(
                            "flex items-center justify-center w-6 h-6 rounded-md transition-transform duration-200",
                            isExpanded && "rotate-90"
                          )}
                          style={{ backgroundColor: nodeDef?.bgColor || '#f1f5f9' }}
                        >
                          <NodeIcon
                            icon={nodeDef?.icon || Database}
                            iconType={nodeDef?.iconType}
                            iconColor={nodeDef?.iconColor}
                            color={nodeDef?.color}
                            className="w-4 h-4"
                          />
                        </div>
                        <span className="text-sm font-medium">
                          {input.nodeName}
                        </span>
                      </div>
                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-7 w-7 p-0"
                              onClick={(e) => {
                                e.stopPropagation();
                                navigator.clipboard.writeText(JSON.stringify(input.data, null, 2));
                              }}
                            >
                              <Copy className="w-3.5 h-3.5" />
                            </Button>
                          </TooltipTrigger>
                          <TooltipContent>
                            <p className="text-xs">Copy all data</p>
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    </button>

                    {/* Table Content */}
                    {isExpanded && (
                      <div className="p-3">
                        {renderTable(input.data)}
                      </div>
                    )}
                  </div>
                );
              })
            )}
          </div>
        </ScrollAreaViewport>
        <ScrollAreaScrollbar orientation="vertical" />
      </ScrollArea>
    </div>
  );
}
